import { useEffect, useMemo, useState } from 'react';

import {
  loadLinkablePages,
  searchLinkablePages,
  type LinkablePage,
} from '@/lib/blocks/page-link-search';
import { getRecentPages, type RecentPage } from '@/lib/navigation/recent-pages';

function recentToLinkable(recent: RecentPage): LinkablePage {
  return {
    id: recent.id,
    title: recent.title,
    type: recent.type,
  };
}

export function useMentionPickerItems(query: string): {
  items: LinkablePage[];
  loading: boolean;
  heading: string;
} {
  const [results, setResults] = useState<LinkablePage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      await loadLinkablePages();
      const next = await searchLinkablePages(query);
      if (!cancelled) {
        setResults(next);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [query]);

  const recents = useMemo(() => {
    const recentPages = getRecentPages().map(recentToLinkable);
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) {
      return recentPages;
    }
    return recentPages.filter((page) => page.title.toLowerCase().includes(trimmed));
  }, [query]);

  const trimmedQuery = query.trim();
  const items = trimmedQuery
    ? results
    : recents.length > 0
      ? recents
      : results.slice(0, 12);

  const heading = trimmedQuery
    ? loading
      ? 'Searching…'
      : 'Pages & databases'
    : recents.length > 0
      ? 'Recents'
      : 'Pages & databases';

  return { items, loading, heading };
}
